import { useState } from "react"
import { Button, Heading, Text } from "@smooth/design-system-components"
import type { GeneratePrompt } from "../types"
import { AlertMessage, Section } from "./parts"
import { PromptFields, usePromptChoice } from "./PromptPicker"

type Props = {
  /** 生成対象のケース。未保存（id 0）の間は生成できない */
  caseId: number
  lockVersion: number
  /** ケースに未保存の変更があるか。生成は保存済みの版に対してだけ行う */
  changed: boolean
  busy: boolean
  /**
   * 生成を実行する。戻り値は「同じ requestKey で再送すべきか」
   * （応答なし・5xx のようにサーバが処理したか分からない失敗のときだけ true）
   */
  onGenerate: (prompt: GeneratePrompt, requestKey: string) => Promise<boolean>
}

/** 生成の意図。同じ入力での再送では requestKey を使い回し、サーバ側で二重生成させない */
type Attempt = { key: string; fingerprint: string }

/** プロンプトを選んで AIドラフトを生成する */
export function GeneratePanel({ caseId, lockVersion, changed, busy, onGenerate }: Props) {
  const choice = usePromptChoice()
  const [attempt, setAttempt] = useState<Attempt | null>(null)
  const { isLoading, error: promptError } = choice
  const canGenerate = !changed && !!caseId && !isLoading && !!choice.prompt && !busy

  const generate = async () => {
    const prompt: GeneratePrompt = choice.prompt!
    const fingerprint = JSON.stringify({ caseId, lockVersion, prompt })
    const key = attempt?.fingerprint === fingerprint ? attempt.key : crypto.randomUUID()
    setAttempt({ key, fingerprint })
    const retryable = await onGenerate(prompt, key)
    if (!retryable) setAttempt(null)
  }

  return (
    <Section>
      <Heading level={3}>AIドラフトを生成</Heading>
      {promptError && <AlertMessage>プロンプト一覧を取得できませんでした。</AlertMessage>}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[24rem] max-w-full">
          <PromptFields choice={choice} />
        </div>
        <Button onClick={generate} disabled={!canGenerate}>
          {busy ? "処理中…" : "AIドラフトを生成"}
        </Button>
      </div>
      {changed && <Text>変更を保存してから生成してください。</Text>}
    </Section>
  )
}
